import { useEffect, useState } from "react";

import { useDocument, useInspectionAttributes } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { API_ENDPOINTS } from "../../../../constants";
import { useProject as useProjectQuery } from "../../../../hooks/query";

export default function useContractSigning() {
    const { project } = useProject();
    const { useListById } = useProjectQuery();
    const listProjectByIdSvc = useListById();
    const { useList, useUpdateStatus, useTriggerContractSigning } = useDocument();
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const listDocListSvc = useList();
    const updateStatusSvc = useUpdateStatus();
    const triggerSigningSvc = useTriggerContractSigning();
    const listAttributesSvc = useListAttributes();

    const [contractDocument, setContractDocument] = useState<any | null>(null);
    const [workOrderDocument, setWorkOrderDocument] = useState<any | null>(null);
    const [purchaseOrderDocument, setPurchaseOrderDocument] = useState<any | null>(null);
    const [hasGutterWork, setHasGutterWork] = useState<boolean>(false);

    const getDocumentStatus = async () => {
        try {
            if (!project?.id) {
                return;
            }
            const response = await listDocListSvc.mutateAsync({ ProjectId: project.id });
            const contractDocument = response.find((document: any) => document.Name === "CONTRACT");
            if (contractDocument) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(contractDocument.ThumbnailAssetId);
                contractDocument.thumbnailUrl = imageUrl;
            }
            setContractDocument(contractDocument);

            const workOrder = response.find((document: any) => document.Name === "WORK_ORDER");
            if (workOrder) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(workOrder.ThumbnailAssetId);
                workOrder.thumbnailUrl = imageUrl;
            }
            setWorkOrderDocument(workOrder);

            const purchaseOrder = response.find((document: any) => document.Name === "SUPPLY_ORDER");
            if (purchaseOrder) {
                const imageUrl = API_ENDPOINTS.asset.getImageAsSrc(purchaseOrder.ThumbnailAssetId);
                purchaseOrder.thumbnailUrl = imageUrl;
            }
            setPurchaseOrderDocument(purchaseOrder);

            const attributes = await listAttributesSvc.mutateAsync({ ProjectId: project.id });


            // Show gutter report if:
            // 1. Install New Gutters is explicitly true (user clicked dropdown), OR
            // 2. Gutter Length > 0 or Gutter Size is set (user entered data but didn't click dropdown — UI shows R&R by default)
            const hasGutter = attributes?.some((attr: any) => {
                const name = attr.AttributeName;
                const val = String(attr.AttributeValue ?? "").toLowerCase();
                if (name === "Install New Gutters" && val === "true") return true;
                if (name === "Gutter Length" && parseFloat(val) > 0) return true;
                if (name === "Gutter Size" && val !== "" && val !== "0") return true;
                return false;
            });
            setHasGutterWork(!!hasGutter);

        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        getDocumentStatus().then();
    }, [project?.id]);

    const markAsSigned = async () => {
        if (!contractDocument?.Id) return;
        await updateStatusSvc.mutateAsync({ DocumentId: contractDocument.Id, Status: "SIGNED" });
        await getDocumentStatus();
    };

    const triggerSigning = async () => {
        try {
            if (!project?.id) return;

            // Get project details to get customer email
            const projectDetails = await listProjectByIdSvc.mutateAsync({ Id: project.id });
            const customerEmail = projectDetails?.CustomerEmail || "";

            if (!customerEmail) {
                throw new Error("Customer email not found");
            }

            await triggerSigningSvc.mutateAsync({
                ProjectId: project.id,
                ToAddress: customerEmail,
            });

            // Refresh document status after sending email
            await getDocumentStatus();
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    return {
        contractDocument,
        workOrderDocument,
        purchaseOrderDocument,
        hasGutterWork,
        markAsSigned,
        triggerSigning,
        isUpdatingStatus: updateStatusSvc.isPending,
        isTriggeringSigning: triggerSigningSvc.isPending,
        refreshContract: getDocumentStatus,
    };
}
